import React from "react";
import { Phone, Navigation, Shield, Star, MapPin, ChevronRight } from "lucide-react";
import { ICON_STROKE } from "../ui/icon";
import {
  TYPE_LABELS,
  formatDistance,
  getDirectionsUrl,
  getOpenStatus,
} from "./nearby-utils";

function getDetailsUrl(service) {
  const directions = getDirectionsUrl(service);
  if (directions) return directions;
  if (service.name && service.address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${service.name} ${service.address}`)}`;
  }
  return null;
}

const NearbyServiceCard = ({ service, onCall }) => {
  const phone = (service.phone || [])[0];
  const distance = formatDistance(service.distanceKm);
  const directionsUrl = getDirectionsUrl(service);
  const detailsUrl = getDetailsUrl(service);
  const openStatus = getOpenStatus(service);
  const rating = service.metadata?.rating;
  const subtypeLabels = {
    major: "Major hospital",
    clinic: "Clinic",
    government: "Government hospital",
    private: "Private hospital",
    "24_hour": "24-hour pharmacy",
  };
  const subLabel =
    (service.serviceType === "hospital" || service.serviceType === "pharmacy"
      ? subtypeLabels[service.subType]
      : null) ||
    service.subType ||
    TYPE_LABELS[service.serviceType] ||
    service.serviceType;

  return (
    <article className="nearby-service-card">
      <div className="nearby-service-card__top">
        <div className="min-w-0 flex-1">
          <h3 className="nearby-service-card__name">{service.name}</h3>
          <p className="nearby-service-card__category">{subLabel}</p>
          {rating != null && (
            <div className="nearby-service-card__rating" aria-label={`Rating ${rating} out of 5`}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={14}
                  strokeWidth={ICON_STROKE}
                  className={star <= Math.round(rating) ? "text-amber-400 fill-amber-400" : "text-muted-foreground/40"}
                />
              ))}
            </div>
          )}
          {service.isVerified && (
            <span className="nearby-service-card__verified">
              <Shield size={12} strokeWidth={ICON_STROKE} />
              Verified
            </span>
          )}
          {openStatus && (
            <p className={`nearby-service-card__status nearby-service-card__status--${openStatus.tone}`}>
              <span aria-hidden>{openStatus.dot}</span> {openStatus.label}
            </p>
          )}
        </div>
        {distance && <span className="nearby-service-card__distance">{distance}</span>}
      </div>

      {service.address && (
        <p className="nearby-service-card__address">
          <MapPin size={15} strokeWidth={ICON_STROKE} className="shrink-0" />
          <span>{service.address}</span>
        </p>
      )}

      <div className="nearby-service-card__actions nearby-service-card__actions--triple">
        {phone ? (
          <button
            type="button"
            className="nearby-service-card__btn nearby-service-card__btn--call"
            onClick={() => onCall({ name: service.name, number: phone })}
          >
            <Phone size={16} strokeWidth={ICON_STROKE} />
            Call
          </button>
        ) : (
          <button type="button" className="nearby-service-card__btn nearby-service-card__btn--call" disabled>
            <Phone size={16} strokeWidth={ICON_STROKE} />
            Call
          </button>
        )}
        {directionsUrl ? (
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="nearby-service-card__btn nearby-service-card__btn--directions"
          >
            <Navigation size={16} strokeWidth={ICON_STROKE} />
            Directions
          </a>
        ) : (
          <button type="button" className="nearby-service-card__btn nearby-service-card__btn--directions" disabled>
            <Navigation size={16} strokeWidth={ICON_STROKE} />
            Directions
          </button>
        )}
        {detailsUrl ? (
          <a
            href={detailsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="nearby-service-card__btn nearby-service-card__btn--details"
          >
            View Details
            <ChevronRight size={16} strokeWidth={ICON_STROKE} />
          </a>
        ) : (
          <button type="button" className="nearby-service-card__btn nearby-service-card__btn--details" disabled>
            View Details
            <ChevronRight size={16} strokeWidth={ICON_STROKE} />
          </button>
        )}
      </div>
    </article>
  );
};

export default NearbyServiceCard;
